import { apiClient } from './api-client';
import {
  type AggregatedDocumentsResponse,
  type DocumentStats,
  DocumentSourceType,
  type AggregatedClientDocument
} from '../types/aggregated-documents';

const API_BASE = '/api/clients';

export const aggregatedDocumentsService = {
  /**
   * Get aggregated documents for a client
   */
  async getAggregatedDocuments(
    clientId: string,
    params?: {
      page?: number;
      limit?: number;
      sourceTypes?: DocumentSourceType[];
      documentTypes?: string[];
      search?: string;
      onlyConfidential?: boolean;
      startDate?: Date;
      endDate?: Date;
      groupBy?: 'source' | 'type' | 'date';
      sortBy?: 'date' | 'name' | 'type' | 'size';
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<AggregatedDocumentsResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.sourceTypes?.length) {
      queryParams.append('sourceTypes', params.sourceTypes.join(','));
    }
    if (params?.documentTypes?.length) {
      queryParams.append('documentTypes', params.documentTypes.join(','));
    }
    if (params?.search) queryParams.append('search', params.search);
    if (params?.onlyConfidential) queryParams.append('onlyConfidential', 'true');
    if (params?.startDate) {
      queryParams.append('startDate', params.startDate.toISOString());
    }
    if (params?.endDate) {
      queryParams.append('endDate', params.endDate.toISOString());
    }
    if (params?.groupBy) queryParams.append('groupBy', params.groupBy);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await apiClient.get(
      `${API_BASE}/${clientId}/documents/aggregated?${queryParams.toString()}`
    );

    return response.data.data;
  },

  /**
   * Get document statistics
   */
  async getDocumentStats(clientId: string): Promise<DocumentStats> {
    const response = await apiClient.get(
      `${API_BASE}/${clientId}/documents/stats`
    );

    return response.data.data.stats;
  },

  /**
   * Search documents
   */
  async searchDocuments(
    clientId: string,
    searchTerm: string
  ): Promise<AggregatedClientDocument[]> {
    const response = await apiClient.get(
      `${API_BASE}/${clientId}/documents/search?q=${encodeURIComponent(searchTerm)}`
    );

    return response.data.data.documents;
  },

  /**
   * Download multiple documents as ZIP
   */
  async downloadMultipleDocuments(
    clientId: string,
    documentIds: string[]
  ): Promise<Blob> {
    const response = await apiClient.post(
      `${API_BASE}/${clientId}/documents/download-multiple`,
      { documentIds },
      { responseType: 'blob' }
    );

    return response.data;
  },

  /**
   * Download a single document
   */
  async downloadDocument(
    doc: AggregatedClientDocument
  ): Promise<Blob> {
    let url: string;

    // Determine the correct endpoint based on the source type
    switch (doc.sourceType) {
      case 'CLIENT':
        url = `/api/clients/${doc.clientId}/documents/${doc.id}/download`;
        break;
      case 'CASE':
        url = `/api/cases/${doc.sourceEntityId}/documents/${doc.id}/download`;
        break;
      case 'TASK':
        url = `/api/attachments/${doc.id}/download`;
        break;
      case 'INTERACTION':
        url = `/api/interactions/${doc.sourceEntityId}/attachments/${doc.id}/download`;
        break;
      case 'FINANCE':
        url = `/api/finance-v2/transactions/${doc.sourceEntityId}/attachments/${doc.id}/download`;
        break;
      default:
        throw new Error(`Unknown source type: ${doc.sourceType}`);
    }

    const response = await apiClient.get(url, { responseType: 'blob' });
    return response.data;
  },

  /**
   * Preview a document
   */
  async previewDocument(
    doc: AggregatedClientDocument
  ): Promise<Blob> {
    let url: string;

    // Determine the correct endpoint based on the source type
    switch (doc.sourceType) {
      case 'CLIENT':
        url = `/api/clients/${doc.clientId}/documents/${doc.id}/preview`;
        break;
      case 'CASE':
        url = `/api/cases/${doc.sourceEntityId}/documents/${doc.id}/preview`;
        break;
      case 'TASK':
        url = `/api/attachments/${doc.id}/download`;
        break;
      case 'INTERACTION':
        url = `/api/interactions/${doc.sourceEntityId}/attachments/${doc.id}/preview`;
        break;
      case 'FINANCE':
        url = `/api/finance-v2/transactions/${doc.sourceEntityId}/attachments/${doc.id}/preview`;
        break;
      default:
        throw new Error(`Unknown source type: ${doc.sourceType}`);
    }

    const response = await apiClient.get(url, { responseType: 'blob' });
    return response.data;
  },

  /**
   * Helper: Download blob as file
   */
  downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Helper: Open blob in new tab for preview
   */
  previewBlob(blob: Blob): void {
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
    // Note: URL will be revoked after some time, not immediately
    setTimeout(() => window.URL.revokeObjectURL(url), 60000); // 1 minute
  },

  /**
   * Helper: Format file size
   */
  formatFileSize(bytes: number | null | undefined): string {
    // Handle invalid values
    if (bytes == null || isNaN(bytes) || bytes < 0) {
      return '0 Bytes';
    }

    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // Ensure index is within bounds
    const sizeIndex = Math.min(i, sizes.length - 1);

    return Math.round(bytes / Math.pow(k, sizeIndex) * 100) / 100 + ' ' + sizes[sizeIndex];
  },

  /**
   * Helper: Get file icon by mime type
   */
  getFileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎬';
    if (mimeType.startsWith('audio/')) return '🎵';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📽️';
    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('compressed')) return '🗜️';
    return '📎';
  },

  /**
   * Helper: Get source type label
   */
  getSourceTypeLabel(sourceType: DocumentSourceType): string {
    const labels: Record<DocumentSourceType, string> = {
      [DocumentSourceType.CLIENT]: 'Cliente',
      [DocumentSourceType.CASE]: 'Caso',
      [DocumentSourceType.TASK]: 'Tarefa',
      [DocumentSourceType.INTERACTION]: 'Atendimento',
      [DocumentSourceType.FINANCE]: 'Financeiro'
    };
    return labels[sourceType] || sourceType;
  },

  /**
   * Helper: Get source type icon
   */
  getSourceTypeIcon(sourceType: DocumentSourceType): string {
    const icons: Record<DocumentSourceType, string> = {
      [DocumentSourceType.CLIENT]: '📁',
      [DocumentSourceType.CASE]: '⚖️',
      [DocumentSourceType.TASK]: '📋',
      [DocumentSourceType.INTERACTION]: '💬',
      [DocumentSourceType.FINANCE]: '💰'
    };
    return icons[sourceType] || '📄';
  }
};
